import type { PromoEvent } from '../data/types'
import { deriveStatus, entryLimitAndCount } from '../data/registrationStatus'
import { formatEventDateRange, formatMoney } from '../data/format'
import { CtaButton } from './StatusBadge'
import styles from './HeroCards.module.css'

/** Shared by RaceTimeHero/FeaturedHero below - both are sitewide paid-promotion cards with the
 *  same flyer-left/body-right layout and the same status/progress-bar/CTA footer ("the racer
 *  count thermometer"), differing only in badge label and dark-vs-light theme. Unified into one
 *  component (rather than two near-duplicates) so the two can never drift apart again. */
function PromoHero({
  event,
  badgeLabel,
  tone,
  canSignUp,
}: {
  event: PromoEvent
  badgeLabel: string
  tone: 'dark' | 'light'
  canSignUp: boolean
}) {
  const href = `/events/displayEvent.cfm?ID=${event.id}`
  const status = deriveStatus(event)
  const { limit, count } = entryLimitAndCount(event)
  const fillPct = canSignUp && limit > 0 ? Math.min(100, Math.round((count / limit) * 100)) : 0
  // Same fallback convention as entryMeta() in EventListCard.tsx: count/limit when there's a
  // limit to measure against, else the raw count, else the class count - but entry counts are
  // racer-facing info, so anonymous visitors and tracks get nothing here (the promoSubline above
  // already shows the class count regardless of login).
  const entryLabel = !canSignUp
    ? ''
    : limit > 0
      ? `${count} / ${limit}`
      : count > 0
        ? `${count} entered`
        : `${event.classCount} class${event.classCount === 1 ? '' : 'es'}`
  const priceFrom = formatMoney(event.priceFrom)
  const toneClass = tone === 'dark' ? styles.promoHeroDark : styles.promoHeroLight

  return (
    <article className={`${styles.promoHero} ${toneClass}`}>
      <a href={href} aria-label={`View the ${event.name} flyer`} className={styles.promoFlyerLink}>
        {event.flyerUrl ? (
          <img
            src={event.flyerUrl}
            alt={`${event.name} flyer - ${formatEventDateRange(event.startDT, event.endDT)} - ${event.trackName}`}
            className={styles.promoFlyerImg}
          />
        ) : null}
      </a>
      <div className={styles.promoBody}>
        <span className={styles.promoBadge}>{badgeLabel}</span>
        <span className={styles.promoDateLine}>
          {formatEventDateRange(event.startDT, event.endDT)} · {event.trackName}
        </span>
        <a href={href} className={styles.promoTitle}>
          {event.name}
        </a>
        <span className={styles.promoSubline}>
          {event.city}, {event.state}
          {priceFrom ? ` · ${event.classCount} classes · from ${priceFrom}` : ` · ${event.classCount} classes`}
        </span>
        <div className={styles.promoFooter}>
          <div className={styles.promoStatusRow}>
            <span style={{ color: status.textColor, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: status.dotColor, display: 'inline-block' }} />
              {status.label}
              {status.unpaid ? <span style={{ color: 'var(--danger)' }}> · Unpaid</span> : null}
            </span>
          </div>
          {canSignUp && limit > 0 ? (
            <div className={styles.promoProgress}>
              <div className={styles.promoProgressFill} style={{ width: `${fillPct}%` }} />
            </div>
          ) : null}
          <div className={styles.promoActionRow}>
            {entryLabel ? <span className={styles.promoMeta}>{entryLabel}</span> : null}
            <CtaButton status={status} href={href} canSignUp={canSignUp} />
          </div>
        </div>
      </div>
    </article>
  )
}

/** RaceTime Entertainment - sitewide paid-partner promo (events.cfc::listEvents(racetime=1)),
 *  kept above Featured per decision 8, styled dark/black. Now matches Featured's layout exactly
 *  (flyer on the left, badge/content on the right, same status/progress/CTA footer) per a later
 *  ask - no longer the sidebar's low-key badge-only treatment. */
export function RaceTimeHero({ event, canSignUp }: { event: PromoEvent; canSignUp: boolean }) {
  return <PromoHero event={event} badgeLabel="RaceTime Entertainment" tone="dark" canSignUp={canSignUp} />
}

/** Featured events - sitewide paid-promotion card (events.cfc::getFeaturedEvents(), a paid
 *  userorders upgrade). Restyled per decision 8 to white background + orange border (matching
 *  the event page sidebar's own FeaturedEventsCard convention) rather than the artboards' black
 *  blurred-flyer hero. */
export function FeaturedHero({ event, canSignUp }: { event: PromoEvent; canSignUp: boolean }) {
  return <PromoHero event={event} badgeLabel="Featured" tone="light" canSignUp={canSignUp} />
}
